
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GitFork, ArrowRight, AlertTriangle, ListOrdered, Merge } from 'lucide-react';
import { PropertyField } from '../../molecules/PropertyField';
import { WorkflowNodeData } from '../../../types';

interface ConditionsTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
    nodeType: string;
}

export const ConditionsTab: React.FC<ConditionsTabProps> = ({ data, onChange, nodeType }) => {
    const [newPathName, setNewPathName] = useState('');
    
    const isExclusive = nodeType === 'XorGateway';
    const isInclusive = nodeType === 'InclusiveGateway';
    const isParallel = nodeType === 'ParallelGateway';
    const isMerging = !!data.isMerging;

    // Ensure outputs array exists
    const outputs = data.outputs || [];
    // Ensure conditions array exists. It might be sparse (e.g. if default path was excluded during import)
    const conditions = data.conditions || [];

    // Auto-initialize if empty
    useEffect(() => {
        if (isMerging) return; // Skip initialization if merging

        if (!data.outputs || data.outputs.length === 0) {
             onChange('outputs', ['default']);
             onChange('conditions', [{
                path: 0,
                expression: '', // Empty expression = default/else path
            }]);
             // Default evaluation order based on type
             if (!data.evaluationOrder) {
                 if (isParallel || isInclusive) onChange('evaluationOrder', 'parallel');
                 else onChange('evaluationOrder', 'sequential');
             }
        }
    }, [data.outputs, isParallel, isInclusive, onChange, data.evaluationOrder, isMerging]);

    const handleAddPath = () => {
        if (!newPathName.trim()) return;
        
        // Add output handle
        const newOutputs = [...outputs, newPathName.trim()];
        
        // Add condition entry. Important: Add explicit 'true' expression so it appears in the list.
        // Empty expressions are treated as hidden default routes.
        const newConditions = [...conditions, {
            path: newOutputs.length - 1,
            expression: 'true',
            priority: newOutputs.length // Default priority
        }];

        onChange('outputs', newOutputs);
        onChange('conditions', newConditions);
        setNewPathName('');
    };

    const handleRemovePath = (index: number) => {
        const newOutputs = outputs.filter((_: any, i: number) => i !== index);
        // Correctly filter out the removed condition AND shift paths for subsequent ones
        const newConditions = conditions
            .filter((c: any) => c.path !== index)
            .map((c: any) => c.path > index ? { ...c, path: c.path - 1 } : c);
            
        onChange('outputs', newOutputs);
        onChange('conditions', newConditions);
    };

    const handleConditionChange = (pathIndex: number, field: string, value: any) => {
        const newConditions = [...conditions];
        const existingIdx = newConditions.findIndex((c: any) => c.path === pathIndex);
        
        if (existingIdx >= 0) {
            newConditions[existingIdx] = { ...newConditions[existingIdx], [field]: value };
        } else {
            // Upsert for sparse arrays
            newConditions.push({ path: pathIndex, expression: '', [field]: value });
        }
        
        // Optional: Keep it sorted by path for data cleanliness
        newConditions.sort((a, b) => a.path - b.path);
        
        onChange('conditions', newConditions);
    };

    const handleRenamePath = (index: number, newName: string) => {
        const newOutputs = [...outputs];
        newOutputs[index] = newName;
        onChange('outputs', newOutputs);
    };

    if (isMerging) {
        return (
            <div className="p-6 flex flex-col items-center justify-center h-full text-center space-y-4 animate-in fade-in duration-300">
                <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-full border-2 border-dashed border-slate-200 dark:border-slate-800">
                    <Merge className="text-slate-400" size={32} />
                </div>
                <div>
                    <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200">Merge Configuration</h3>
                    <p className="text-xs text-slate-500 max-w-xs mx-auto mt-2 leading-relaxed">
                        This gateway acts as a join node, consolidating multiple incoming flows into a single sequence flow. 
                    </p>
                    <p className="text-xs text-slate-400 max-w-xs mx-auto mt-1 italic">
                        Path configuration is disabled for merging gateways.
                    </p>
                </div>
            </div>
        );
    }

    const getDescription = () => {
        if (isParallel) return "Define the output paths for parallel execution. All connected paths will be activated simultaneously.";
        if (isInclusive) return "Define paths and conditions. All matching paths will be activated.";
        return "Define output paths and the boolean conditions that trigger them. Only one path will be selected.";
    };

    const showConditions = !isParallel;
    const showEvaluationOrder = isExclusive;
    const isPriorityMode = data.evaluationOrder === 'priority';

    return (
        <div className="p-6 space-y-6">
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-900 flex gap-3">
                <GitFork className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" size={16} />
                <div className="space-y-1">
                    <p className="text-xs font-bold text-amber-800 dark:text-amber-300">
                        {isParallel ? 'Parallel Split' : (isInclusive ? 'Inclusive Routing' : 'Exclusive Routing')}
                    </p>
                    <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-relaxed">
                        {getDescription()}
                    </p>
                </div>
            </div>

            {showEvaluationOrder && (
                <div className="space-y-3">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Evaluation Order</label>
                    <select 
                        value={data.evaluationOrder || 'sequential'}
                        onChange={(e) => onChange('evaluationOrder', e.target.value)}
                        className="w-full px-3 py-2 text-sm border rounded-lg border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20"
                    >
                        <option value="sequential">Sequential (First Match)</option>
                        <option value="priority">Priority Based</option>
                    </select>
                </div>
            )}
            
            {!showEvaluationOrder && !isParallel && (
                <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded border border-slate-100 dark:border-slate-800 flex items-center gap-2 text-xs text-slate-500">
                    <ListOrdered size={14} />
                    <span>Evaluation Order: <strong>All Matches (Parallel)</strong></span>
                </div>
            )}

            {isParallel && (
                <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded border border-slate-100 dark:border-slate-800 flex items-center gap-2 text-xs text-slate-500">
                    <GitFork size={14} />
                    <span>Execution: <strong>Simultaneous (All Paths)</strong></span>
                </div>
            )}

            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Paths {showConditions && '& Conditions'}</label>
                </div>
                
                <div className="space-y-4">
                    {outputs.map((pathName: string, index: number) => {
                        // Look up condition by path index, handle sparse/missing
                        const condition = conditions.find((c: any) => c.path === index);
                        const isDefaultPath = !condition || !condition.expression || condition.expression.trim() === '';

                        // Default path: show with special styling, renamable but no expression
                        if (isDefaultPath) {
                            return (
                                <div key={index} className="relative p-3 bg-amber-50/50 dark:bg-amber-900/10 rounded-xl border border-amber-200 dark:border-amber-800 shadow-sm group transition-all">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 flex-1 mr-4">
                                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-[10px] font-bold text-amber-600 border border-amber-200 dark:border-amber-700 shrink-0">
                                                {index + 1}
                                            </span>
                                            <input
                                                type="text"
                                                value={pathName}
                                                onChange={(e) => handleRenamePath(index, e.target.value)}
                                                className="w-full text-sm font-bold text-amber-800 dark:text-amber-200 bg-transparent border-b border-transparent focus:border-amber-500 outline-none transition-colors"
                                                placeholder="Default Path Name"
                                            />
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700 whitespace-nowrap">
                                                Default (Else)
                                            </span>
                                        </div>
                                    </div>
                                    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <ArrowRight className="text-amber-500" size={16} />
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={index} className="relative p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm group transition-all hover:border-teal-300 dark:hover:border-teal-700">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2 flex-1 mr-4">
                                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-800 text-[10px] font-bold text-slate-500 border border-slate-200 dark:border-slate-700 shrink-0">
                                            {index + 1}
                                        </span>
                                        <input
                                            type="text"
                                            value={pathName}
                                            onChange={(e) => handleRenamePath(index, e.target.value)}
                                            className="w-full text-sm font-bold text-slate-800 dark:text-slate-100 bg-transparent border-b border-transparent focus:border-teal-500 outline-none transition-colors"
                                            placeholder="Path Name"
                                        />
                                    </div>
                                    <button
                                        onClick={() => handleRemovePath(index)}
                                        className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>

                                {showConditions && (
                                    <div className="space-y-2 pl-7">
                                        <div className="flex gap-4">
                                            <div className="flex-1">
                                                <PropertyField
                                                    config={{
                                                        name: `condition_${index}`,
                                                        label: 'Expression',
                                                        type: 'expression',
                                                        placeholder: 'e.g. variables.amount > 500 (Clear to make Default)'
                                                    }}
                                                    value={condition.expression}
                                                    onChange={(_, val) => handleConditionChange(index, 'expression', val)}
                                                />
                                            </div>
                                            {isPriorityMode && (
                                                <div className="w-24">
                                                    <PropertyField
                                                        config={{
                                                            name: `priority_${index}`,
                                                            label: 'Priority',
                                                            type: 'number',
                                                            placeholder: '1'
                                                        }}
                                                        value={condition.priority}
                                                        onChange={(_, val) => handleConditionChange(index, 'priority', val)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ArrowRight className="text-teal-500" size={16} />
                                </div>
                            </div>
                        );
                    })}

                    {outputs.length === 0 && (
                        <div className="text-center py-6 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                            <AlertTriangle className="mx-auto text-slate-300 mb-2" size={24}/>
                            <p className="text-xs text-slate-500">No output paths defined.</p>
                        </div>
                    )}
                </div>

                <div className="flex gap-2 pt-2">
                    <input 
                        type="text" 
                        value={newPathName}
                        onChange={(e) => setNewPathName(e.target.value)}
                        placeholder="New Path Name (e.g. Approved)" 
                        className="flex-1 px-3 py-2 text-sm border rounded-lg border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddPath()}
                    />
                    <button 
                        onClick={handleAddPath}
                        disabled={!newPathName.trim()}
                        className="px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                        <Plus size={16} />
                    </button>
                </div>
            </div>
            
            {showConditions && (
                <div className="bg-slate-50 dark:bg-slate-900 p-3 rounded-lg text-[11px] text-slate-500 border border-slate-100 dark:border-slate-800">
                    <span className="font-bold text-slate-700 dark:text-slate-300">Tip:</span> Use variables like <code>variables.total</code> in your expressions. Any path with an <strong>empty</strong> expression is hidden from this list and becomes the <strong>Default (Else)</strong> route.
                </div>
            )}
        </div>
    );
};
